import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, ChefHat, ArrowLeft, Flame, Leaf, Sprout } from "lucide-react";
import { getRecipe } from "@/lib/api";
import { metaFor } from "@/lib/cuisines";

export default function RecipePage() {
  const { id } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    getRecipe(id).then(setRecipe).catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <div className="max-w-7xl mx-auto px-6 py-32 text-center" data-testid="recipe-not-found">
        <h1 className="font-heading text-4xl mb-4">Recipe not found</h1>
        <Link to="/" className="text-gray-900 underline">
          Back to home
        </Link>
      </div>
    );

  if (!recipe)
    return (
      <div className="max-w-7xl mx-auto px-6 py-32 text-center text-gray-400">Loading…</div>
    );

  const meta = metaFor(recipe.cuisine);

  return (
    <div data-testid="recipe-detail-page" className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-16">
      <Link
        to={`/cuisine/${recipe.cuisine}`}
        data-testid="recipe-back-link"
        className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 mb-10"
      >
        <ArrowLeft className="h-4 w-4" /> Back to {meta.name}
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left column */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <motion.div
              initial={{ opacity: 0, scale: 1.03 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="overflow-hidden aspect-[4/5]"
            >
              <img
                src={recipe.image}
                alt={recipe.name}
                className="w-full h-full object-cover"
                data-testid="recipe-hero-image"
              />
            </motion.div>
            <div className="mt-6 flex flex-wrap gap-2">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${meta.badge}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} /> {meta.name}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">
                <Clock className="h-3.5 w-3.5" /> {recipe.cook_time}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">
                <ChefHat className="h-3.5 w-3.5" /> {recipe.difficulty}
              </span>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="lg:col-span-7">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-heading text-5xl sm:text-6xl font-light tracking-tighter leading-none text-gray-900"
            data-testid="recipe-title"
          >
            {recipe.name}
          </motion.h1>
          <p className="mt-5 text-lg text-gray-600 font-light leading-relaxed">
            {recipe.description}
          </p>

          {/* Origin */}
          <div className="mt-12">
            <h2 className="text-xs font-bold uppercase tracking-[0.25em] text-gray-500 mb-4">
              Origin &amp; Story
            </h2>
            <p className="text-base md:text-lg text-gray-700 font-light leading-relaxed">
              {recipe.origin}
            </p>
          </div>

          {/* Spices */}
          <div className="mt-12">
            <h2 className="text-xs font-bold uppercase tracking-[0.25em] text-gray-500 mb-5 flex items-center gap-2">
              <Flame className="h-4 w-4" /> Main Spices &amp; Their Role
            </h2>
            <div className="space-y-4" data-testid="recipe-spices">
              {recipe.spices.map((s, i) => (
                <div key={i} className={`border-l-2 pl-4 ${meta.border}`}>
                  <p className="font-heading text-xl text-gray-900">{s.name}</p>
                  <p className="text-sm text-gray-600 font-light leading-relaxed">{s.role}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-12 mt-12">
            {/* Ingredients */}
            <div>
              <h2 className="text-xs font-bold uppercase tracking-[0.25em] text-gray-500 mb-5">
                Ingredients
              </h2>
              <ul className="space-y-3" data-testid="recipe-ingredients">
                {recipe.ingredients.map((ing, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-800">
                    <span className={`mt-2 h-1.5 w-1.5 rounded-full flex-shrink-0 ${meta.dot}`} />
                    <span className="font-light">{ing}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Steps */}
            <div>
              <h2 className="text-xs font-bold uppercase tracking-[0.25em] text-gray-500 mb-5">
                Method
              </h2>
              <ol className="space-y-6" data-testid="recipe-steps">
                {recipe.steps.map((step, i) => (
                  <li key={i} className="flex gap-4">
                    <span className="font-heading text-3xl font-light text-gray-300 leading-none">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="text-gray-800 font-light leading-relaxed pt-1">{step}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* Veg / Vegan alternatives */}
          {(recipe.veg_alternative || recipe.vegan_alternative) && (
            <div className="mt-12" data-testid="recipe-alternatives">
              <h2 className="text-xs font-bold uppercase tracking-[0.25em] text-gray-500 mb-5">
                Veg &amp; Vegan Alternatives
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {recipe.veg_alternative && (
                  <div className="border border-green-200 bg-green-50 p-5" data-testid="recipe-veg-alt">
                    <p className="flex items-center gap-2 text-sm font-semibold text-green-800 mb-2">
                      <Leaf className="h-4 w-4" /> Vegetarian
                    </p>
                    <p className="text-sm text-gray-700 font-light leading-relaxed">
                      {recipe.veg_alternative}
                    </p>
                  </div>
                )}
                {recipe.vegan_alternative && (
                  <div className="border border-emerald-200 bg-emerald-50 p-5" data-testid="recipe-vegan-alt">
                    <p className="flex items-center gap-2 text-sm font-semibold text-emerald-800 mb-2">
                      <Sprout className="h-4 w-4" /> Vegan
                    </p>
                    <p className="text-sm text-gray-700 font-light leading-relaxed">
                      {recipe.vegan_alternative}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
